import {useState} from "react";
import {supabase} from "../../services/supabase";
import BlogEditor from "./BlogEditor";

export default function EditBlog({ blog, onClose, onUpdate }) {
    const [title, setTitle] = useState(blog.title);
    const [content, setContent] = useState(() => {
        try {
            const parsed = JSON.parse(blog.content);
            return Array.isArray(parsed) ? parsed : [{ type: "paragraph", children: [{ text: blog.content }] }];
        } catch {
            return [{ type: "paragraph", children: [{ text: blog.content }] }];
        }
    });
    const [image, setImage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [currentImageUrl] = useState(blog.thumbnail_url);

    const handleUpdate = async (e) => {
        e.preventDefault();
        setSaving(true);
        let newImageUrl = currentImageUrl;

        if (image) {
            const fileExt = image.name.split(".").pop();
            const fileName = `thumbnails/${Date.now()}.${fileExt}`;
            const { error: uploadError } = await supabase.storage
                .from("blog-images")
                .upload(fileName, image);

            if (uploadError) {
                console.error(uploadError);
                setSaving(false);
                return;
            }

            const { data: publicUrlData } = supabase.storage
                .from("blog-images")
                .getPublicUrl(fileName);

            newImageUrl = publicUrlData.publicUrl;
        }

        const { error } = await supabase
            .from("blogs")
            .update({
                title,
                content: JSON.stringify(content),
                thumbnail_url: newImageUrl,
            })
            .eq("id", blog.id);

        if (error) {
            console.error(error);
        } else {
            setSuccess(true);
            onUpdate();
            setTimeout(() => {
                setSuccess(false);
                onClose();
            }, 1500);
        }

        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-auto p-4">
            <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 text-gray-500 hover:text-red-500"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-3xl font-bold text-gray-900 mb-6">Edit Blog Post</h2>

                <form onSubmit={handleUpdate}>
                    {/* Title */}
                    <div className="mb-6">
                        <label className="block text-sm font-semibold mb-2">Title</label>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full border border-gray-300 rounded px-4 py-3 text-gray-800"
                            required
                        />
                    </div>

                    {/* Current Thumbnail + Upload */}
                    <div className="mb-6">
                        <label className="block text-sm font-semibold mb-2">Thumbnail Image</label>
                        {currentImageUrl && (
                            <img loading="lazy"
                                src={currentImageUrl}
                                alt="Thumbnail"
                                className="w-full h-56 object-cover mb-3 rounded"
                            />
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setImage(e.target.files[0])}
                            className="w-full border rounded px-4 py-2"
                        />
                    </div>

                    {/* Blog Content */}
                    <div className="mb-6">
                        <label className="block text-sm font-semibold mb-2">Content</label>
                        <BlogEditor
                            content={content}
                            setContent={setContent}
                        />
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        className="bg-orange-600 text-white px-6 py-3 rounded hover:bg-orange-700 font-semibold"
                    >
                        {saving ? "Saving..." : "Save Changes"}
                    </button>
                    {success && <p className="text-green-600 mt-3">Blog updated successfully!</p>}
                </form>
            </div>
        </div>
    );
}
